import _ from 'lodash'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { compose } from 'recompose'

import { NEUTRAL_COLOURS } from 'constants/colors'
import { DISPLAY_DATE_SHORT_MONTH_NAME_FORMAT, DISPLAY_HOURS_MINUTES_FORMAT } from 'constants/date'
import { Child } from 'services/child/constants'
import {
  RegistrationForm,
  RegistrationFormStatus,
  STATUS_BADGE_COLOR,
  STATUS_LABELS,
} from 'services/registrationForms/models'

import { withRegistrationFormsService, withRegistrationFormsServiceProps } from 'services/registrationForms'
import { withModalService, withModalServiceProps } from 'services/utils/modal'

import { Banner, Button, DetailsBreakdown, DropdownMenu, Flex, Icon, Space, Typography } from 'components'
import { ModalType } from 'modals'

import i18n from 'translations'

import { StyledBadge } from './RegistrationFormBreakdownStyled'

interface RegistrationFormBreakdownProps {
  child: Child
  hasBookingPattern: boolean
  onRegistrationFormSendSubmitSuccess: () => void
  onToggleExpand: () => void
  registrationFormIncludeBookingPattern?: boolean
}

type RegistrationFormBreakdownFinalProps = RegistrationFormBreakdownProps
  & withModalServiceProps
  & withRegistrationFormsServiceProps

const RegistrationFormBreakdown: React.FC<RegistrationFormBreakdownFinalProps> = ({
  child,
  hasBookingPattern,
  modalActions,
  onRegistrationFormSendSubmitSuccess,
  onToggleExpand,
  registrationFormIncludeBookingPattern,
  registrationFormsActions,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const [downloadItemId, setDownloadItemId] = useState<number>()

  const { registrationForms } = child
  const latestItemStatus = registrationForms?.length
    ? registrationForms[registrationForms.length - 1].status
    : RegistrationFormStatus.NOT_SENT

  useEffect(() => {
    onToggleExpand()
  }, [isExpanded])

  const handleSendClick = () => {
    modalActions.show<ModalType.SEND_REGISTRATION_FORM>(ModalType.SEND_REGISTRATION_FORM, {
      child,
      onSubmitSuccess: onRegistrationFormSendSubmitSuccess,
    })
  }

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded)
  }

  const handleViewClick = (item: RegistrationForm) => {
    setDownloadItemId(item.id)

    registrationFormsActions.download({
      onFailed: () => {
        setDownloadItemId(undefined)
      },
      onSuccess: ({ data }) => {
        setDownloadItemId(undefined)

        const link = document.createElement('a')
        link.setAttribute('target', '_blank')
        link.href = data.url
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      },
      onlyData: true,
      params: [item.id],
    })
  }

  const renderContent = () => (
    <React.Fragment>
      <Typography color={NEUTRAL_COLOURS.GRAY} fontSize={14} padding="6px 0 0 0">
        {i18n.t('module:Children:Child:About:Profile:RegistrationForm:description')}
      </Typography>
      {registrationForms?.length ? (
        <Flex flexDirection="column" gap="16px" margin="16px 0 0 0">
          {_.map(registrationForms, (item) => (
            <Flex alignItems="center" justifyContent="space-between" key={item.id}>
              <Flex flexDirection="column" gap="4px">
                <Typography fontSize={16} bold>
                  {RegistrationFormStatus.SENT === item.status
                    ? i18n.t('module:Children:Child:About:Profile:RegistrationForm:sentToItemLabel', {
                      name: item.carer?.fullName,
                    })
                    : i18n.t('module:Children:Child:About:Profile:RegistrationForm:completedByItemLabel', {
                      name: item.carer?.fullName,
                    })}
                </Typography>
                <Typography color={NEUTRAL_COLOURS.GRAY} fontSize={14}>
                  {
                      `${moment(item.updatedAt).format(DISPLAY_DATE_SHORT_MONTH_NAME_FORMAT)
                      }, ${moment(item.updatedAt).format(DISPLAY_HOURS_MINUTES_FORMAT)}`
                    }
                </Typography>
              </Flex>
              {RegistrationFormStatus.COMPLETED === item.status && (
                <Button
                  hierarchy="tertiary"
                  isLoading={downloadItemId === item.id}
                  label={i18n.t('global:View')}
                  margin="-8px 4px -8px 0"
                  onClick={() => handleViewClick(item)}
                />
              )}
            </Flex>
          ))}
        </Flex>
      ) : undefined}
      <Space space="10px" />
    </React.Fragment>
  )

  const renderAction = () => {
    if (RegistrationFormStatus.SENT === latestItemStatus || RegistrationFormStatus.LINK_EXPIRED === latestItemStatus) {
      return (
        <DropdownMenu minWidth={150}>
          <DropdownMenu.Item icon="resend" label={_.upperFirst(i18n.t('global:resend'))} onClick={handleSendClick} />
        </DropdownMenu>
      )
    }

    if (RegistrationFormStatus.COMPLETED === latestItemStatus) {
      return (
        <DropdownMenu minWidth={150}>
          <DropdownMenu.Item icon="send" label={_.upperFirst(i18n.t('global:send'))} onClick={handleSendClick} />
        </DropdownMenu>
      )
    }

    return (
      <Button hierarchy="tertiary" label={i18n.t('global:Send')} margin="-8px 4px -8px 0" onClick={handleSendClick} />
    )
  }

  return (
    <DetailsBreakdown marginBottom>
      <DetailsBreakdown.Section>
        <Flex alignItems="center" justifyContent="space-between" padding="10px 0">
          <Flex alignItems="center" gap="10px" justifyContent="center">
            <Typography fontSize={28} fontWeight={300} padding="0 16px 0 0" variant="span">
              {i18n.t('module:Children:Child:About:Profile:RegistrationForm:title')}
            </Typography>
            <StyledBadge $color={STATUS_BADGE_COLOR[latestItemStatus]} />
            <Typography variant="span">
              {STATUS_LABELS[latestItemStatus]}
            </Typography>
          </Flex>
          <Flex
            alignItems="center"
            gap={RegistrationFormStatus.NOT_SENT !== latestItemStatus && '10px'}
            justifyContent="center"
          >
            {renderAction()}
            <Icon height={20} icon={!isExpanded ? 'chevron-down' : 'chevron-up'} onClick={handleToggleExpand} />
          </Flex>
        </Flex>
        {(!hasBookingPattern && registrationFormIncludeBookingPattern) && (
          <React.Fragment>
            <Space space="6px" />
            <Banner.Warning>
              {i18n.t('module:Children:Child:About:Profile:RegistrationForm:emptyBookingPattern')}
            </Banner.Warning>
          </React.Fragment>
        )}
        {isExpanded && renderContent()}
      </DetailsBreakdown.Section>
    </DetailsBreakdown>
  )
}

const enhance = compose(
  withModalService,
  withRegistrationFormsService,
)

export default enhance(RegistrationFormBreakdown)
